import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './App.css';

function Home() {
  const heroImageRef = useRef(null);
  const feature1ImageRef = useRef(null);
  const feature2ImageRef = useRef(null);
  const feature3ImageRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px 0px -100px 0px'
    };

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in-visible');
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Capture ref values in variables
    const heroElement = heroImageRef.current;
    const feature1Element = feature1ImageRef.current;
    const feature2Element = feature2ImageRef.current;
    const feature3Element = feature3ImageRef.current;

    if (heroElement) {
      observer.observe(heroElement);
    }
    if (feature1Element) {
      observer.observe(feature1Element);
    }
    if (feature2Element) {
      observer.observe(feature2Element);
    }
    if (feature3Element) {
      observer.observe(feature3Element);
    }

    return () => {
      if (heroElement) {
        observer.unobserve(heroElement);
      }
      if (feature1Element) {
        observer.unobserve(feature1Element);
      }
      if (feature2Element) {
        observer.unobserve(feature2Element);
      }
      if (feature3Element) {
        observer.unobserve(feature3Element);
      }
    };
  }, []);

  return (
    <div className="App">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="logo">
            <img src="/app-icon.png" alt="Orbit Rings Logo" className="logo-icon" />
            <span className="logo-text">Orbit Rings</span>
          </div>
          <div className="nav-links">
            <Link to="/terms">Terms of Service</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <h1 className="hero-title">
              Orbit Rings. Visual Task Tracking
            </h1>
            <h2 className="hero-subtitle">
              Track up to <span className="gradient-text">three custom tasks</span> with visual progress rings.
            </h2>
            <div className="hero-buttons">
              <a href="https://apps.apple.com/us/app/orbit-progress-rings/id6755077967" className="btn btn-primary" target="_blank" rel="noopener noreferrer">
                <img src="/app-icon.png" alt="App Icon" className="apple-icon" />
                Open App Store
              </a>
            </div>
          </div>
          <div className="hero-image fade-in" ref={heroImageRef}>
            <img
              src="/images/all-sc.png"
              alt="Orbit Rings Apple Watch App Screenshots"
              className="watch-screenshots"
            />
          </div>
        </div>
      </section>

      {/* Feature 1: Custom Rings */}
      <section className="feature-section">
        <div className="feature-container">
          <div className="feature-content">
            <h2 className="feature-title">Custom Rings</h2>
            <p className="feature-description">
              Track up to three custom activities using visual rings to represent your daily progress.
              Configure on iPhone, track actively on Apple Watch.
            </p>
            <p className="feature-description">
              Each trackable can be customized with icons, your preferred color, custom target durations,
              and selected days of the week you want to track.
            </p>
          </div>
          <div className="feature-image-container fade-in" ref={feature1ImageRef}>
            <img
              src="/images/watch-rings.png"
              alt="Custom Orbit Rings on Apple Watch"
              className="feature-screenshot"
            />
          </div>
        </div>
      </section>

      {/* Feature 2: Track Progress */}
      <section className="feature-section feature-section-alt">
        <div className="feature-container feature-container-reverse">
          <div className="feature-image-container fade-in" ref={feature2ImageRef}>
            <img
              src="/images/watch-progress.png"
              alt="Track Progress on Apple Watch"
              className="feature-screenshot"
            />
          </div>
          <div className="feature-content">
            <h2 className="feature-title">Start, Track, Your Progress</h2>
            <p className="feature-description">
              Start, pause, and resume tracking sessions with real-time progress updates right on your wrist.
            </p>
            <p className="feature-description">
              See elapsed time, total time today, and remaining time at a glance. Track your activities
              throughout the day with simple controls.
            </p>
          </div>
        </div>
      </section>

      {/* Feature 3: Widgets */}
      <section className="feature-section">
        <div className="feature-container">
          <div className="feature-content">
            <h2 className="feature-title">Custom Widgets</h2>
            <p className="feature-description">
              Monitor your progress right from your watch face with widget support.
            </p>
            <p className="feature-description">
              Add your progress rings to your favorite watch face and see your daily completion status
              at a glance. All your data syncs seamlessly across your devices.
            </p>
          </div>
          <div className="feature-image-container fade-in" ref={feature3ImageRef}>
            <img
              src="/images/watch-wigets.png"
              alt="Custom Widgets on Apple Watch"
              className="feature-screenshot"
            />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about">
        <div className="about-container">
          <h2 className="about-title gradient-text-alt">Orbit Rings.</h2>
          <p className="about-subtitle">A task tracking app using visual rings to represent progress.</p>
          <p className="about-description">
            Orbit Rings helps you track up to three custom activities throughout your day. Configure your trackables on your iPhone
            with custom names, icons, colors, and target durations. Track actively on your Apple Watch with
            real-time progress rings. All your data syncs seamlessly across your devices, and you can monitor
            your progress right from your watch face with widget support.
          </p>
        </div>
      </section>
    </div>
  );
}

export default Home;
